/**
 * EditableText — Renders text in the report; when admin + edit mode is on,
 * shows a small pen icon that opens <EditFieldModal> (TipTap-powered).
 *
 * Save commits immediately on the modal's Tallenna button.
 */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Pencil, Check, Loader2 } from 'lucide-react';
import { useReportEdits } from './ReportEditsContext';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import EditFieldModal from './EditFieldModal';
import Markdown from './Markdown';

const Wrap = styled.span`
  position: relative;
  display: ${p => (p.$block ? 'block' : 'inline')};
`;

const PenBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  width: 22px;
  height: 22px;
  padding: 0;
  border-radius: 4px;
  border: 1px solid rgba(124, 58, 237, 0.25);
  background: rgba(124, 58, 237, 0.08);
  color: #7c3aed;
  cursor: pointer;
  vertical-align: baseline;
  transform: translateY(2px);
  transition: background 0.12s;
  &:hover {
    background: rgba(124, 58, 237, 0.18);
    border-color: rgba(124, 58, 237, 0.6);
  }
  @media print { display: none; }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  color: #7c3aed;
  background: rgba(124, 58, 237, 0.08);
  border-radius: 4px;
  padding: 2px 6px;
  vertical-align: middle;
  user-select: none;
  @media print { display: none; }
`;

const SavingBadge = styled(Badge)`
  color: #6b7280;
  background: rgba(107, 114, 128, 0.1);
  svg { animation: spin 1s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

/**
 * Props:
 * - fieldKey (required): stable key for storage (e.g. 'exec.key_findings')
 * - fallback (required): original text rendered when no edit exists
 * - label (optional): human-readable label shown in the edit modal header
 * - multiline (optional, default true): enables block-level toolbar items
 * - block (optional): render container as block vs inline
 */
export default function EditableText({
  fieldKey,
  fallback,
  label,
  multiline = true,
  block = false,
  // Optional display-time transformer for the current text. Used by the
  // scenario ingress to strip leftover markdown headings (`# Title…`)
  // regardless of whether they came from the AI-generated fallback or a
  // fasilitaattori's edit. The raw value stays in report_edits — only the
  // rendered output is transformed.
  transform,
  // When true, render the resolved text as markdown (paragraphs, bold,
  // italics, lists). Body text fields whose AI-generated value uses
  // markdown — scenario summaries, pivot descriptions, lens summaries —
  // should set this so `**word**` shows as bold instead of literal
  // asterisks. The raw value in report_edits stays markdown-encoded so
  // the edit modal still shows what the field actually contains.
  markdown = false,
  // When set, wraps the rendered text in <a href={link} target="_blank">.
  // Suppressed in edit mode so the pen / edit affordance still wins the
  // click. Used by the Tietopohja list to make titles hyperlinkable.
  link,
  // Sibling field key for an editable URL. When provided, the edit modal
  // also exposes a Link input — handy for the Tietopohja list where the
  // title and URL belong together.
  linkFieldKey,
  linkFallback,
  linkLabel,
  // Live substitution map for `{token}` placeholders in the text. The
  // RAW value in report_edits keeps the `{tokens}` (so a fasilitaattori
  // edits the words while the live counts stay bound) — only the rendered
  // output has them filled in. Used by e.g. the Vipupisteet summary:
  // `"{aiCount} vipupisteestä valittiin {selectedCount} jatkotyöskentelyyn."`
  vars,
}) {
  const { t } = useTranslation();
  const { canEdit, editMode, get, isEdited, savingKeys, isTranslatedView } = useReportEdits();
  const [modalOpen, setModalOpen] = useState(false);

  const rawText = get(fieldKey, fallback);
  const transformed = typeof transform === 'function' ? transform(rawText) : rawText;
  const currentText = (vars && typeof transformed === 'string')
    ? transformed.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m))
    : transformed;
  const edited = isEdited(fieldKey);
  const saving = savingKeys.has(fieldKey);
  // Editing is bound to the instance language — hide the pen in translated views
  const editableNow = canEdit && editMode && !isTranslatedView;

  // In view mode, an explicit link wraps the rendered text in an <a>.
  // We never link while editing — the pen affordance has to win clicks.
  const linkUrl = !editableNow && typeof link === 'string' && link.trim() ? link.trim() : null;
  const renderedText = markdown ? <Markdown>{currentText}</Markdown> : currentText;
  const linked = linkUrl
    ? <a href={linkUrl} target="_blank" rel="noreferrer">{renderedText}</a>
    : renderedText;

  return (
    <Wrap $block={block}>
      {linked}
      {editableNow && (
        <PenBtn type="button" onClick={() => setModalOpen(true)} aria-label="Muokkaa">
          <Pencil size={12} />
        </PenBtn>
      )}
      {/* "Muokattu" badge is admin-only — it's metadata about who edited
       * what, not useful for the public report viewer. Sitra-signed-in
       * admins still see it (when they're not in edit mode; in edit mode
       * the pen icon already covers the affordance). */}
      {edited && canEdit && !editMode && (
        <Badge title="Admin on muokannut tätä kenttää">
          <Check size={11} /> {t('report.sources.editedBadge')}
        </Badge>
      )}
      {saving && (
        <SavingBadge>
          <Loader2 size={11} /> Tallennetaan…
        </SavingBadge>
      )}
      {modalOpen && (
        <EditFieldModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          fieldKey={fieldKey}
          label={label}
          fallback={fallback}
          multiline={multiline}
          linkFieldKey={linkFieldKey}
          linkFallback={linkFallback}
          linkLabel={linkLabel}
        />
      )}
    </Wrap>
  );
}
